import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Drawer,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    resetExperienceFilters,
    selectExperienceFilters,
    setExperienceFilters,
} from '../../../../app/Slices/ExperienceFiltersSlice';
import {
    resetExperienceSearchFilter,
    selectExperienceSearchFilter,
    setExperienceSearchFilter,
} from '../../../../app/Slices/ExperienceSearchSlice';
import DropdownFilter from '../../../../layout/styledcomponents/filters/Dropdown';
import { capitalizeName } from '../../../../shared/utils';

const MODULE_TYPE_OPTIONS = [
    { value: '', label: 'All types' },
    { value: 'property', label: 'Property' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'boat', label: 'Boat' },
    { value: 'activity', label: 'Activity' },
    { value: 'vehicle', label: 'Vehicle' },
];

const STATUS_OPTIONS = [
    { value: 'all', label: 'All statuses' },
    { value: 'published', label: 'Published' },
    { value: 'unpublished', label: 'Unpublished' },
    { value: 'draft', label: 'Draft' },
];

const CAPACITY_OPTIONS = [
    { value: '', label: 'Any' },
    { value: '1', label: '1+' },
    { value: '2', label: '2+' },
    { value: '3', label: '3+' },
    { value: '4', label: '4+' },
    { value: '5', label: '5+' },
    { value: '6', label: '6+' },
    { value: '7', label: '7+' },
    { value: '8', label: '8+' },
    { value: '9', label: '9+' },
    { value: '10', label: '10+' },
];

export type OwnerListingStatusFilter =
    | 'all'
    | 'published'
    | 'unpublished'
    | 'draft';

type OwnerListingsHeroProps = {
    experiences?: Array<{
        suburb?: string;
        state?: string;
        countryCode?: string;
    }>;
    statusFilter: OwnerListingStatusFilter;
    onStatusFilterChange: (value: OwnerListingStatusFilter) => void;
    locationFilter: string;
    onLocationFilterChange: (value: string) => void;
};

export default function OwnerListingsHero({
    experiences = [],
    statusFilter,
    onStatusFilterChange,
    locationFilter,
    onLocationFilterChange,
}: OwnerListingsHeroProps) {
    const theme = useTheme();
    const showMobileFilter = useMediaQuery('(max-width: 1360px)');
    const dispatch = useDispatch();
    const experienceFilters = useSelector(selectExperienceFilters);
    const searchValue = useSelector(selectExperienceSearchFilter);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    const filterSurface = theme.palette.background.paper;
    const filterBorder = theme.palette.divider;
    const controlRadius = '6px';
    const controlHeight = 36;

    const locationOptions = useMemo(() => {
        const base = [{ value: 'all', label: 'All locations' }];
        const states = new Set<string>();
        experiences.forEach((experience) => {
            if (experience.state?.trim()) {
                states.add(capitalizeName(experience.state.trim()));
            }
        });
        return [
            ...base,
            ...Array.from(states)
                .sort((a, b) => a.localeCompare(b))
                .map((state) => ({ value: state, label: state })),
        ];
    }, [experiences]);

    const activeType = experienceFilters.type?.[0] ?? '';
    const typeLabel =
        MODULE_TYPE_OPTIONS.find((option) => option.value === activeType)
            ?.label ?? 'All types';
    const statusLabel =
        STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label ??
        'All statuses';
    const locationLabel =
        locationFilter === 'all'
            ? 'All locations'
            : (locationOptions.find((option) => option.value === locationFilter)
                  ?.label ?? 'All locations');
    const capacityLabel =
        CAPACITY_OPTIONS.find(
            (option) => option.value === (experienceFilters.guests || ''),
        )?.label ?? 'Any';

    const filterDropdownButtonSx = {
        borderRadius: controlRadius,
        marginRight: 0,
        marginBottom: 0,
        overflow: 'visible',
        minWidth: 'min-content',
    };

    const filterTrigger = (label: string, value: string) => (
        <>
            <Typography
                component='span'
                sx={{ fontSize: 14, fontWeight: 400, textTransform: 'none' }}
            >
                {label}
            </Typography>
            <Typography
                component='span'
                sx={{ fontSize: 14, fontWeight: 700, textTransform: 'none' }}
            >
                {value}
            </Typography>
        </>
    );

    const handleSearchValueChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        dispatch(setExperienceSearchFilter(event.target.value));
    };

    const handleTypeChange = (value: string) => {
        dispatch(
            setExperienceFilters({
                ...experienceFilters,
                type: value ? [value] : [],
            }),
        );
    };

    const handleCapacityChange = (value: string) => {
        dispatch(
            setExperienceFilters({
                ...experienceFilters,
                guests: value,
            }),
        );
    };

    const handleClearFilters = () => {
        dispatch(resetExperienceSearchFilter());
        dispatch(resetExperienceFilters());
        onStatusFilterChange('all');
        onLocationFilterChange('all');
        setFilterSheetOpen(false);
    };

    useEffect(() => {
        if (
            locationFilter !== 'all' &&
            !locationOptions.some((option) => option.value === locationFilter)
        ) {
            onLocationFilterChange('all');
        }
    }, [locationFilter, locationOptions, onLocationFilterChange]);

    const searchTextField = (
        <TextField
            fullWidth
            size='small'
            placeholder='Listing name or location'
            value={searchValue}
            onChange={handleSearchValueChange}
            InputProps={{
                startAdornment: (
                    <InputAdornment position='start'>
                        <SearchIcon
                            sx={{ color: 'text.secondary', fontSize: 20 }}
                        />
                    </InputAdornment>
                ),
            }}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: controlRadius,
                    minHeight: controlHeight,
                    py: 0,
                    bgcolor: theme.palette.background.paper,
                    '& fieldset': {
                        borderColor: theme.palette.lightGrey.main,
                    },
                    '&:hover fieldset': {
                        borderColor: theme.palette.lightGrey.main,
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                    },
                },
                '& .MuiOutlinedInput-input': {
                    py: '7px',
                    fontSize: 14,
                },
            }}
        />
    );

    const renderDropdown = (
        id: string,
        options: Array<{ value: string; label: string }>,
        handleChange: (value: string) => void,
        label: string,
        value: string,
        direction: 'row' | 'column',
        flexBasis = 150,
    ) => (
        <Box
            sx={{
                minWidth: 0,
                width: direction === 'column' ? '100%' : undefined,
                flex: direction === 'row' ? `1 1 ${flexBasis}px` : undefined,
            }}
        >
            <DropdownFilter
                id={id}
                options={options}
                handleChange={handleChange}
                selectedValue={filterTrigger(label, value)}
                backgroundColor={filterSurface}
                border={filterBorder}
                minHeight={`${controlHeight}px`}
                maxHeight={`${controlHeight}px`}
                buttonSx={filterDropdownButtonSx}
            />
        </Box>
    );

    const clearFiltersButton = (
        <Button
            type='button'
            variant='outlined'
            size='small'
            onClick={handleClearFilters}
            startIcon={<FilterListOffIcon sx={{ fontSize: 18 }} />}
            sx={{
                boxSizing: 'border-box',
                height: controlHeight,
                minHeight: controlHeight,
                maxHeight: controlHeight,
                py: 0,
                px: 1.25,
                borderRadius: controlRadius,
                borderColor: 'divider',
                textTransform: 'none',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: 1,
                color: 'text.secondary',
                minWidth: 'auto',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                '&:hover': {
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                },
            }}
        >
            Clear filters
        </Button>
    );

    const filterFields = (direction: 'row' | 'column') => (
        <Stack
            direction={direction}
            alignItems={direction === 'row' ? 'center' : 'stretch'}
            flexWrap={direction === 'row' ? 'nowrap' : 'wrap'}
            gap={1}
            sx={{
                width: direction === 'column' ? '100%' : 'auto',
                minWidth: 0,
                flex: direction === 'row' ? '1 1 auto' : undefined,
            }}
        >
            {renderDropdown(
                'listingTypeFilter',
                MODULE_TYPE_OPTIONS,
                handleTypeChange,
                'Listing type: ',
                typeLabel,
                direction,
            )}
            {renderDropdown(
                'listingStatusFilter',
                STATUS_OPTIONS,
                (value) =>
                    onStatusFilterChange(value as OwnerListingStatusFilter),
                'Status: ',
                statusLabel,
                direction,
            )}
            {renderDropdown(
                'listingLocationFilter',
                locationOptions,
                onLocationFilterChange,
                'Location: ',
                locationLabel,
                direction,
                170,
            )}
            {renderDropdown(
                'listingCapacityFilter',
                CAPACITY_OPTIONS,
                handleCapacityChange,
                'Capacity: ',
                capacityLabel,
                direction,
            )}
            <Box
                sx={{
                    flexShrink: 0,
                    alignSelf:
                        direction === 'column' ? 'flex-start' : undefined,
                }}
            >
                {clearFiltersButton}
            </Box>
        </Stack>
    );

    if (showMobileFilter) {
        return (
            <>
                <Stack direction='row' spacing={1} alignItems='stretch'>
                    <Box sx={{ flex: 1, minWidth: 0 }}>{searchTextField}</Box>
                    <Button
                        type='button'
                        variant='outlined'
                        size='small'
                        onClick={() => setFilterSheetOpen(true)}
                        startIcon={<FilterListIcon />}
                        sx={{
                            flexShrink: 0,
                            height: controlHeight,
                            minHeight: controlHeight,
                            px: 1.5,
                            borderRadius: controlRadius,
                            borderColor: theme.palette.lightGrey.main,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 14,
                            color: 'text.primary',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Filter
                    </Button>
                </Stack>
                <Drawer
                    anchor='bottom'
                    open={filterSheetOpen}
                    onClose={() => setFilterSheetOpen(false)}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            maxHeight: '88vh',
                        },
                    }}
                >
                    <Box sx={{ px: 2, pt: 2, pb: 3 }}>
                        <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            sx={{ mb: 2 }}
                        >
                            <Typography variant='subtitle1' fontWeight={600}>
                                Filters
                            </Typography>
                            <Button
                                size='small'
                                onClick={() => setFilterSheetOpen(false)}
                                sx={{ textTransform: 'none' }}
                            >
                                Close
                            </Button>
                        </Stack>
                        {filterFields('column')}
                    </Box>
                </Drawer>
            </>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'nowrap',
                minWidth: 0,
                overflowX: 'auto',
                pb: 0.25,
            }}
        >
            <Box
                sx={{
                    flex: '1 1 220px',
                    minWidth: 180,
                    maxWidth: 320,
                    flexShrink: 1,
                }}
            >
                {searchTextField}
            </Box>
            {filterFields('row')}
        </Box>
    );
}
